import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import {
  StarOutline, Star, Twitter, YouTube, LinkedIn, Instagram, Web, Facebook,
} from '@mui/icons-material';
import { theme, loadMedia } from '../../lib/index';

function ReaderRatingLinkLabel({ readerRatingLink }) {
  if (readerRatingLink.indexOf('twitter') !== -1) return <Twitter />;
  if (readerRatingLink.indexOf('facebook') !== -1) return <Facebook />;
  if (readerRatingLink.indexOf('instagram') !== -1) return <Instagram />;
  if (readerRatingLink.indexOf('youtube') !== -1) return <YouTube />;
  if (readerRatingLink.indexOf('linkedin') !== -1) return <LinkedIn />;

  return <Web />;
}

export default function CardRating({ rating }) {
  const [image, setImage] = useState({ id: '', src: '', alt: '' });
  const stars = Array(5).fill(0);

  useEffect(() => {
    if (rating.reader_rating_photo && rating.reader_rating_photo !== '') {
      loadMedia(rating.reader_rating_photo).then(setImage);
    } else {
      loadMedia(1098).then(setImage);
    }
  }, [rating]);

  if (!rating) {
    return null;
  }

  return (
    <Card>
      <InnerBox>
        <Image src={image.src} alt={image.alt} />
        <TextBox>
          <Name variant="h6">
            {rating.reader_rating_name}
          </Name>
          {stars.map((_, index) => (
            parseInt(rating.reader_rating_stars, 10) > index
              ? <FilledStar key={index} />
              : <OutlinedStar key={index} />
          ))}
          <Date>
            Rezension vom
            {' '}
            {`${
              rating.reader_rating_date.slice(6)
            }.${
              rating.reader_rating_date.slice(4, 6)
            }.${
              rating.reader_rating_date.slice(0, 4)
            }`}
          </Date>
        </TextBox>
      </InnerBox>
      {
        rating.reader_rating_text.length > 210
          ? (
            <CustomTooltip title={rating.reader_rating_text} arrow>
              <Despcription>{rating.reader_rating_text}</Despcription>
            </CustomTooltip>
          )
          : <Despcription>{rating.reader_rating_text}</Despcription>
      }

      {rating.reader_rating_link && (
        <a
          href={rating.reader_rating_link.indexOf('https://') || rating.reader_rating_link.indexOf('http://')
            ? rating.reader_rating_link
            : `https://${rating.reader_rating_link}`}
          target="_blank"
          rel="noopener noreferrer"
          className="link"
        >
          <ReaderRatingLinkLabel
            readerRatingLink={rating.reader_rating_link}
          />
        </a>
      )}
    </Card>
  );
}

// Styling
const Card = styled(Box, {})({
  position: 'relative',

  padding: '25px',
  width: '100%',
  maxWidth: '65vw',
  height: '100%',

  background: theme.palette.white,
  boxShadow: `0 0 7px ${theme.palette.black}20`,

  transition: 'box-shadow 250ms ease-in-out, transform 250ms ease-in-out',

  '&:hover': {
    boxShadow: `0px 0px 7px ${theme.palette.black}40`,

    transform: 'scale(1.01)',

    transition: 'box-shadow 250ms ease-in-out, transform 250ms ease-in-out',
  },

  [theme.breakpoints.up('laptop')]: {
    width: 'calc( 50% - 70px)',
    height: 230,
  },

  ' .link': {
    position: 'absolute',
    top: 25,
    right: 25,
    color: theme.palette.black,
  },
});

const InnerBox = styled(Box, {})({
  display: 'flex',
  marginBottom: 20,

  gap: 20,
  alignItems: 'center',
});

const TextBox = styled(Box, {})({
  display: 'inline-block',
});

const Name = styled(Typography, {})({
  ...theme.typography.pBold17,
});

const Date = styled(Typography, {})({
  ...theme.typography.pSmallLight,
});

const Image = styled('img', {})({
  display: 'inline-block',
  width: 100,
  height: 100,

  borderRadius: '50%',

  aspectRatio: '1/1',
  objectFit: 'cover',
});

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    ...theme.typography.p,

    padding: 20,
    maxWidth: 500,

    background: theme.palette.backgroundgrey,
    boxShadow: '3px 3px 10px black',

    whiteSpace: 'pre-wrap',
    color: theme.palette.fontgrey,
    fontSize: 16,
    lineHeigt: '28px',
  },
}));

const Despcription = styled(Box, {})({
  ...theme.typography.pSmall,

  [theme.breakpoints.up('laptop')]: {
    ...theme.typography.p16,

    display: '-webkit-box',
    overflow: 'hidden',
    height: 100,

    textOverflow: 'ellipsis',
    whiteSpace: 'pre-wrap',
    WebkitLineClamp: '5',
    WebkitBoxOrient: 'vertical',
  },
});

const FilledStar = styled(Star, {})({
  color: theme.palette.hdc,
  fontSize: 14,
});

const OutlinedStar = styled(StarOutline, {})({
  color: theme.palette.hdc,
  fontSize: 14,
});
